import React, { useState, useCallback } from 'react';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { TodoItem } from '../../types/todo';
import { STATUS_ICONS, PRIORITY, MESSAGES } from '../../utils/constants';

interface TodoNodeHeaderProps {
  id: string;
  item: TodoItem;
  hasChildren: boolean;
  onToggleDone: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUpdateName: (name: string) => void;
  isDraggable: boolean;
  attributes?: DraggableAttributes;
  listeners?: SyntheticListenerMap;
  childrenIndicator?: React.ReactNode;
  /** Right-cluster controls (time estimate + action buttons) rendered in the header row */
  controls?: React.ReactNode;
  isExpanded?: boolean;
  onToggleExpand?: (e: React.MouseEvent) => void;
}

const TodoNodeHeader: React.FC<TodoNodeHeaderProps> = ({
  id,
  item,
  hasChildren,
  onToggleDone,
  onUpdateName,
  isDraggable,
  attributes,
  listeners,
  childrenIndicator,
  controls,
  isExpanded,
  onToggleExpand,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(item.name);

  const getStatusIcon = useCallback(() => {
    if (item.done) return STATUS_ICONS.DONE;
    if (item.blocked) return STATUS_ICONS.BLOCKED;
    if (item.archived) return STATUS_ICONS.ARCHIVED;
    return STATUS_ICONS.PENDING;
  }, [item.done, item.blocked, item.archived]);

  const getPriorityIcon = useCallback(() => {
    return PRIORITY.ICONS[item.priority] || PRIORITY.ICONS.low;
  }, [item.priority]);

  const getPriorityStyle = useCallback((): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {};

    switch (item.priority) {
      case 'medium':
        baseStyle.fontStyle = 'italic';
        break;
      case 'high':
        baseStyle.fontWeight = 'bold';
        break;
      case 'critical':
        baseStyle.fontWeight = 'bold';
        baseStyle.color = 'var(--priority-critical)';
        break;
    }

    return baseStyle;
  }, [item.priority]);

  const handleNameDoubleClick = useCallback(() => {
    if (!item.archived) {
      setIsEditingName(true);
      setEditName(item.name);
    }
  }, [item.name, item.archived]);

  const handleNameSave = useCallback(() => {
    if (editName.trim() && editName !== item.name) {
      onUpdateName(editName.trim());
    }
    setIsEditingName(false);
  }, [editName, item.name, onUpdateName]);

  const handleNameKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      setIsEditingName(false);
      setEditName(item.name);
    }
  }, [handleNameSave, item.name]);

  const getStatusTitle = useCallback(() => {
    if (item.done) return 'Done';
    if (item.blocked) return 'Blocked';
    if (item.archived) return 'Archived';
    return 'Pending';
  }, [item.done, item.blocked, item.archived]);

  return (
    <div className="node-header">
      {isDraggable && !item.archived && (
        <span
          className="drag-handle"
          title={MESSAGES.DRAG_TOOLTIP}
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </span>
      )}

      <input
        type="checkbox"
        checked={item.done}
        onChange={onToggleDone}
        className="done-checkbox"
        disabled={item.archived}
      />

      <span
        className="status-icon"
        title={getStatusTitle()}
      >
        {getStatusIcon()}
      </span>

      <span
        className="priority-icon"
        title={`Priority: ${item.priority}`}
      >
        {getPriorityIcon()}
      </span>

      {isEditingName ? (
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onBlur={handleNameSave}
          onKeyDown={handleNameKeyPress}
          className="node-name-edit"
          autoFocus
        />
      ) : (
        <span
          className="node-name"
          onDoubleClick={handleNameDoubleClick}
          style={getPriorityStyle()}
          title={item.archived ? item.name : MESSAGES.EDIT_NAME_TOOLTIP}
        >
          {item.name}
        </span>
      )}

      {childrenIndicator}

      {item.visibility === 'hide-all' && (
        <span className="hide-children-warning" title="Children hidden">⚠️</span>
      )}

      {controls}

      {onToggleExpand && (
        <button
          type="button"
          className="expand-toggle"
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
          title={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          <span className="expand-caret" aria-hidden="true">▾</span>
        </button>
      )}
    </div>
  );
};

export default TodoNodeHeader;